import { HttpClient } from '@angular/common/http';
import {isDevMode, ModuleWithProviders, NgModule} from '@angular/core';
import {clientIssue, serverIssue} from '@im-js/dev/report';
import {failedInitialize} from '@im-ng/core';
import {PREFERENCE_WORD_SERVICE} from '@im-ng/token/preference';
import {fusion, toFilterString} from '@js-dot/core';
import {DotCoreService} from '@ng-dot/core';
import {DOT_TRANSLATE_MODULE_CONFIGURE, DotTranslateModule, DotTranslateModuleConfigure, HtmlLang} from '@ng-dot/text';
import {EnglishPluralExpression} from '@ng-dot/text/en';
import {KoreanPostPosition} from '@ng-dot/text/ko';
import {isWordId} from './functions/is-word-id';

@NgModule({
	imports: [
		DotTranslateModule.forRoot()
	],
	exports: [
		DotTranslateModule
	]
})
export class TextModule {
	/**
	 * 언어 모듈 활성화
	 * @example
	 * ```ts
	 * @NgModule({
	 *     imports: [
	 *         TextModule.forRoot()
	 *     ]
	 * })
	 * export class AppModule {
	 * }
	 * ```
	 * @alias DotTranslateModule.forRoot
	 * @param configure
	 */
	static forRoot(configure?: Partial<DotTranslateModuleConfigure>): ModuleWithProviders<DotTranslateModule> {
		return {
			ngModule: TextModule,
			providers: [
				{
					provide: DOT_TRANSLATE_MODULE_CONFIGURE,
					deps: [
						DotCoreService,
						HttpClient
					],
					useFactory: textProvideFactory(configure)
				}
			]
		}
	}
}

function textProvideFactory(configure?: Partial<DotTranslateModuleConfigure>) {
	return (_core: DotCoreService, _httpClient: HttpClient) => {

		const defaultConfigure: Partial<DotTranslateModuleConfigure> = {
			isWordId: isWordId,
			wordIds: [
				'global'
			],
			phraseMethods: [
				EnglishPluralExpression,
				KoreanPostPosition
			]
		};

		function localWordLoader(lang: HtmlLang, id: string) {
			return new Promise<Record<string, string>>(resolve => {
				_httpClient.get<Record<string, string>>(`/assets/texts/${id}/${lang}/${id}.json`)
					.subscribe(words => {
						resolve(words);
					});
			})
		}

		function localPhraseLoader(lang: HtmlLang, id: string) {
			const module = id.split('-')[0];

			return new Promise<Record<string, string>>(resolve => {
				_httpClient.get<Record<string, string>>(`/assets/texts/${module}/${lang}/phrases/${id}.json`)
					.subscribe(phrase => {
						resolve(phrase);
					});
			})
		}

		if (_core.includes(PREFERENCE_WORD_SERVICE)) {
			const _word                   = _core.inject(PREFERENCE_WORD_SERVICE);
			defaultConfigure.initialize = () => _word.httpClient.onHostChanged();
			defaultConfigure.wordLoader = (lang, id) => {
				return new Promise((resolve, reject) => {
					const parameters = {
						key: 'key',
						value: 'value',
						$filter: toFilterString({
							lang,
							group: id
						})
					};

					_word.$with.request(parameters as any).subscribe({
						next: isDevMode() ?
							words => {
								if (Object.keys(words).length === 0) {
									console.debug(...serverIssue(`요청된 구성 단어 집합에서 단어를 찾을 수 없습니다.`, {
										api: _word.$with.url,
										source: parameters
									}))
									localWordLoader(lang, id)
										.then(response => {
											console.debug(...clientIssue(`서버에 없는 ${id} 집합의 구성 단어를, *.json 파일로 대체 했습니다.`));
											resolve(response);
										})
										.catch(reason => {
											console.debug(...clientIssue(`서버와 단말 모두 ${id} 집합의 구성 단어를 포함하고 있지 않습니다.`));
										});
								}
								resolve(words);
							} :
							words => {
								resolve(words);
							}
						,
						error: textError
					});
				});
			}

		} else {
			defaultConfigure.wordLoader = localWordLoader;
		}

		defaultConfigure.phraseLoader = localPhraseLoader;

		return fusion(configure, defaultConfigure);
	}
}

function textError(reason: Error) {
	failedInitialize(
		'Cannot load the Preference words',
		reason
	);
}